// DEV-ONLY local login — sign in at http://localhost:5173 WITHOUT Keycloak
// and WITHOUT any console snippet. Opening the app auto-signs you in (default
// the seeded super-admin); a small dev switcher hops between users to see the
// scope tiers (gm / a Müdür / a Personel).
//
// CARDINAL SAFETY RULE (task brief): DEV-ONLY. Everything here is gated behind
// `isDevAuthActive()` (→ `import.meta.env.DEV`), so a production build never
// reaches this code and Vite tree-shakes the module out entirely. Production
// auth (the Keycloak authorization-code + PKCE redirect) is byte-identical to
// today.
//
// How it works (no oidc storage-key hardcoding — version-proof):
//   1. The backend dev-login endpoint (GET /v1/dev/login, the OTHER agent's
//      contract) mints the SAME customer HS256 JWT mintDevToken() produces,
//      only with an 8h TTL, plus the OIDC profile claims.
//   2. buildDevUser() turns { token, expires_at, profile } into an
//      oidc-client-ts `User`.
//   3. bootstrapDevAuth() constructs a UserManager from the SAME settings
//      oidc-config.ts feeds react-oidc-context (authority / client_id /
//      userStore) and calls storeUser(). Because the settings match, the user
//      lands under the EXACT key react-oidc-context's own UserManager reads
//      (oidc.user:<authority>:<client_id>) — so AuthProvider hydrates it on
//      mount and RequireAuth never calls signinRedirect() against the (down)
//      Keycloak.

import { User, UserManager, type UserManagerSettings, type UserProfile } from "oidc-client-ts";

import {
  oidcAuthority,
  oidcClientId,
  oidcRedirectUri,
  oidcUserStore,
} from "@/auth/oidc-config";
import { isDevAuthActive } from "@/auth/dev-auth-flag";
import { platformGraphBaseUrl } from "@/api/platform-graph";

export { isDevAuthActive };

// ---------------------------------------------------------------------------
// Contract — GET /v1/dev/login (served by the backend on the platform-graph
// base URL). Typed locally because these dev-only shapes are NOT part of the
// generated OpenAPI client (the prod schema never carries the dev endpoint).
// ---------------------------------------------------------------------------

/** The OIDC profile claims the backend returns for a chosen dev user. */
export interface DevLoginProfile {
  readonly sub: string;
  readonly preferred_username: string;
  readonly name: string;
  readonly email: string;
  readonly locale: string;
  readonly iss: string;
  readonly tenant_id: string;
  readonly roles: readonly string[];
}

/** One row of GET /v1/dev/login's user list — drives the switcher. */
export interface DevLoginUserSummary {
  readonly key: string;
  readonly name: string;
  readonly role: string;
  readonly email: string;
}

/** GET /v1/dev/login (no ?user) success payload. */
export interface DevLoginIndex {
  readonly users: readonly DevLoginUserSummary[];
  readonly tenant_id: string;
  readonly issuer: string;
}

/** GET /v1/dev/login?user=<key> success payload. */
export interface DevLoginSession {
  readonly token: string;
  /** unix seconds — equals the JWT exp. */
  readonly expires_at: number;
  readonly profile: DevLoginProfile;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key holding the currently-selected dev user (survives reloads). */
export const DEV_AUTH_USER_STORAGE_KEY = "novemai.devAuthUser";

/** Fallback dev user when none is chosen — the seeded super-admin (Genel Müdür). */
export const DEFAULT_DEV_USER_KEY = "gm";

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested)
// ---------------------------------------------------------------------------

/**
 * The storage key react-oidc-context's UserManager reads its user from:
 * `oidc.user:<authority>:<client_id>` (the WebStorageStateStore `oidc.`
 * prefix + UserManager's `user:<authority>:<client_id>`). We WRITE via
 * storeUser() (which derives this internally) — this helper exists only so a
 * test can assert the key the library lands on, and so callers can clear it.
 */
export function devUserStorageKey(authority: string, clientId: string): string {
  return `oidc.user:${authority}:${clientId}`;
}

/**
 * Build an oidc-client-ts `User` from the backend dev-login session. The
 * resulting object, once stored, is what react-oidc-context exposes as
 * `auth.user` (so `auth.user.access_token` flows into the API client's Bearer
 * injector exactly like a real Keycloak login).
 *
 * `aud` is set to the client_id and `exp`/`iat` are derived from `expires_at`
 * so the profile satisfies oidc-client-ts's UserProfile (Mandatory iss/sub/
 * aud/exp/iat). The access token itself is the authority on the wire — the
 * server re-verifies the HS256 signature; these client-side profile fields are
 * only for the SPA's own user object.
 */
export function buildDevUser(session: DevLoginSession, clientId: string): User {
  const { token, expires_at, profile } = session;
  const userProfile: UserProfile = {
    ...profile,
    aud: clientId,
    exp: expires_at,
    // No iat on the wire profile; approximate it as exp − 8h (the dev TTL).
    // Only affects the client-side User object, never the verified token.
    iat: expires_at - 8 * 60 * 60,
  };
  return new User({
    access_token: token,
    id_token: token,
    token_type: "Bearer",
    scope: "openid profile email",
    profile: userProfile,
    expires_at,
  });
}

/**
 * The `sub` a dev-login token carries for a roster key. Mirrors the backend's
 * `devSub()` (auth/dev-roster.ts) — the only field on the stored user that
 * identifies WHICH persona the live session belongs to.
 */
export function devUserSub(key: string): string {
  return `dev-${key}`;
}

/**
 * True iff `user` is the live session for `key` — unexpired AND minted for
 * that persona.
 *
 * The identity half is the load-bearing one. The predicate used to check
 * expiry only, so bootstrapDevAuth() treated "some valid user is stored" as
 * "the selected user is signed in". Writing novemai.devAuthUser directly (the
 * browser-verify recipe) then produced a screen that named the new persona in
 * the switcher while every request still carried the old one's token — a
 * scope check run that way passes without testing anything.
 */
export function storedUserMatchesKey(user: User, key: string): boolean {
  return user.expired === false && user.profile.sub === devUserSub(key);
}

/** True iff the stored OIDC user is the live session for `key`. */
function hasValidStoredUserFor(key: string): boolean {
  if (oidcUserStore === undefined) return false;
  const raw = window.sessionStorage.getItem(
    devUserStorageKey(oidcAuthority, oidcClientId),
  );
  if (raw === null) return false;
  try {
    return storedUserMatchesKey(User.fromStorageString(raw), key);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Selected-user accessors (localStorage)
// ---------------------------------------------------------------------------

/** Read the chosen dev user key (defaults to the super-admin). */
export function getSelectedDevUserKey(): string {
  if (typeof window === "undefined") return DEFAULT_DEV_USER_KEY;
  return window.localStorage.getItem(DEV_AUTH_USER_STORAGE_KEY) ?? DEFAULT_DEV_USER_KEY;
}

/** Persist the chosen dev user key for subsequent reloads. */
export function setSelectedDevUserKey(key: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DEV_AUTH_USER_STORAGE_KEY, key);
}

/** Remove the stored OIDC user so AuthProvider hydrates as signed-out. */
function clearStoredOidcUser(): void {
  if (oidcUserStore === undefined) return;
  window.sessionStorage.removeItem(devUserStorageKey(oidcAuthority, oidcClientId));
}

// ---------------------------------------------------------------------------
// Network helpers
// ---------------------------------------------------------------------------

/** Build a UserManager whose settings match react-oidc-context's exactly. */
function devUserManager(): UserManager {
  const settings: UserManagerSettings = {
    authority: oidcAuthority,
    client_id: oidcClientId,
    redirect_uri: oidcRedirectUri,
    // Only set when defined (exactOptionalPropertyTypes); callers gate on
    // oidcUserStore !== undefined before reaching here.
    ...(oidcUserStore !== undefined ? { userStore: oidcUserStore } : {}),
  };
  return new UserManager(settings);
}

/** Fetch the seeded dev-user list (for the switcher). Returns [] on any error. */
export async function fetchDevUsers(): Promise<readonly DevLoginUserSummary[]> {
  try {
    const res = await fetch(`${platformGraphBaseUrl}/v1/dev/login`, { credentials: "include" });
    if (!res.ok) return [];
    const body: unknown = await res.json();
    const data = (body as { data?: DevLoginIndex }).data;
    return data?.users ?? [];
  } catch {
    return [];
  }
}

/** Fetch a minted session for one dev user. Returns null on any error. */
async function fetchDevSession(key: string): Promise<DevLoginSession | null> {
  try {
    const res = await fetch(
      `${platformGraphBaseUrl}/v1/dev/login?user=${encodeURIComponent(key)}`,
      { credentials: "include" },
    );
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const data = (body as { data?: DevLoginSession }).data;
    return data ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Bootstrap + switcher actions
// ---------------------------------------------------------------------------

/**
 * DEV-ONLY: ensure a valid OIDC user exists in sessionStorage BEFORE
 * AuthProvider mounts, so RequireAuth never kicks off a Keycloak redirect.
 *
 * No-op (returns immediately) when dev-auth is inactive — i.e. always in a
 * production build — and when the stored user IS the selected persona. Any backend
 * error is swallowed: the app then falls through to the normal (down-Keycloak)
 * path, which simply shows the existing "Sign-in failed" state rather than
 * crashing on boot.
 */
export async function bootstrapDevAuth(): Promise<void> {
  if (!isDevAuthActive()) return;
  if (oidcUserStore === undefined) return;

  const key = getSelectedDevUserKey();
  if (hasValidStoredUserFor(key)) return;

  const session = await fetchDevSession(key);
  if (session === null) return;

  const user = buildDevUser(session, oidcClientId);
  await devUserManager().storeUser(user);
}

/**
 * Switcher action: select a different dev user. Persists the choice, clears
 * the current OIDC user, and reloads so bootstrapDevAuth() re-runs and signs
 * in as the newly-chosen user.
 */
export function selectDevUser(key: string): void {
  setSelectedDevUserKey(key);
  clearStoredOidcUser();
  window.location.assign("/");
}

/**
 * Dev "Sign out": clear the stored OIDC user and reload. bootstrapDevAuth()
 * re-runs on the fresh load and re-signs-in as the selected user (there is no
 * real IdP to sign out of — the point of dev-auth is staying signed in).
 */
export function signOutDevAuth(): void {
  clearStoredOidcUser();
  window.location.assign("/");
}
